import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "../lib/seo";
import { MaskText, Reveal, ParallaxImage } from "../lib/motion";
import { IMG } from "../data/site";

const STEPS = [
  { n: "01", t: "Underarbete", d: "Ett bra golv börjar under ytan. Vi lägger stor vikt vid förberedelse och jämnt underlag." },
  { n: "02", t: "Läggning", d: "Parkett och trägolv läggs med precision – mönster, skarvar och passning ska stämma." },
  { n: "03", t: "Finish", d: "Detaljerna avgör helheten. Vi arbetar noggrant hela vägen till färdigt resultat." },
];

export default function Golvlaggning() {
  return (
    <>
      <Seo
        title="Golvläggning i Skåne & Göteborg · Casagon"
        description="Casagon lägger parkett och trägolv för privatpersoner och företag i hela Skåne och hela Göteborg. Över 10 års erfarenhet av golvläggning."
        path="/golvlaggning"
      />

      {/* Hero */}
      <section className="grain relative overflow-hidden bg-graphite pb-16 pt-32 text-warmwhite md:pb-24 md:pt-44" data-testid="golv-hero">
        <div className="u-container relative z-10">
          <Reveal>
            <p className="overline text-wood">
              <span className="mr-2 inline-block h-2 w-2 bg-casared align-middle" />
              Vår specialitet
            </p>
          </Reveal>
          <MaskText
            as="h1"
            animateOnLoad
            delay={0.15}
            lines={["Golvläggning i", "parkett och trägolv."]}
            className="mt-6 font-display font-bold leading-[0.95] tracking-tight text-[8vw] sm:text-6xl lg:text-7xl"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-warmwhite/70 md:text-lg">
              Casagon har lång erfarenhet av golvläggning och lägger bland annat parkett och trägolv
              för privatpersoner och företag i hela Skåne och hela Göteborg.
            </p>
          </Reveal>
        </div>
        <div className="u-container relative z-10 mt-14">
          <ParallaxImage
            src={IMG.floor}
            alt="Nylagt trägolv i ljust rum"
            amount={40}
            priority
            className="aspect-[16/9] w-full"
          />
        </div>
      </section>

      {/* Process / finish */}
      <section className="bg-warmwhite py-20 md:py-24" data-testid="golv-process">
        <div className="u-container">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
              <MaskText
                as="h2"
                lines={["Ett hantverk", "från grund", "till finish."]}
                className="font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl"
              />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-xs text-base leading-relaxed text-graphite/70">
                  Vi bygger golvet steg för steg – med fokus på det som avgör slutresultatet.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08} className="hairline flex gap-6 py-6 md:gap-10 md:py-8">
                  <span className="font-display text-sm font-bold text-casared">{s.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{s.t}</h3>
                    <p className="mt-3 max-w-md text-base leading-relaxed text-graphite/70">{s.d}</p>
                  </div>
                </Reveal>
              ))}
              <div className="hairline" />
            </div>
          </div>
        </div>
      </section>

      {/* Private & company + images */}
      <section className="bg-paper py-16 md:py-24" data-testid="golv-segments">
        <div className="u-container grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <ParallaxImage src={IMG.staircase} alt="Trägolv och trappa i bostad" amount={50} className="aspect-[4/5]" />
          </div>
          <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="overline text-wood">Privat & företag</p>
            </Reveal>
            <MaskText
              as="h2"
              lines={["Golv för hem", "och projekt."]}
              className="mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl"
            />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-graphite/70">
                Vi lägger golv i privata bostäder såväl som i större projekt för företag i hela
                Skåne och hela Göteborg. Oavsett omfattning arbetar vi med samma fokus på
                underarbete, precision och finish.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="grain relative overflow-hidden bg-graphite py-14 text-warmwhite md:py-16" data-testid="golv-service-area">
        <div className="u-container relative z-10 grid grid-cols-1 items-end gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="overline text-casared">Arbetsområde</p>
            <MaskText
              as="h2"
              lines={["Hela Skåne.", "Hela Göteborg."]}
              className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
            />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-warmwhite/70 md:text-lg">
                Vi utgår från Höör men tar golvuppdrag i hela Skåne och hela Göteborg. Berätta var
                projektet finns och vad du vill göra, så återkommer vi med nästa steg.
              </p>
              <Link
                to="/kontakt"
                data-testid="golv-service-area-cta"
                className="link-underline mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em]"
              >
                Begär offert <ArrowUpRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>


    </>
  );
}
